import OfferDetailsModal from '@/components/features/Leads/components/OfferDetailsModal';
import RejectionModal from '@/components/features/Leads/components/RejectionModal';
import EditOfferModal from '@/components/features/Leads/forms/EditOfferModal';
import { GenericToolbar } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { useSettings } from '@/contexts/SettingsContext';
import { useColumnVisibility } from '@/hooks/use-column-visibility';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { csrfFetch } from '@/lib/csrfFetch';
import { settingLabel } from '@/lib/status-labels';
import { cn, formatCurrency, useCan } from '@/lib/utils';
import { Lead, LeadOffer } from '@/types';
import { router } from '@inertiajs/react';
import { format } from 'date-fns';
import { Check, Edit2, Eye, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

interface OffersListProps {
    lead: Lead;
    offers: LeadOffer[];
}

const OFFER_STATUSES = ['pending', 'approved', 'rejected'] as const;

/** Sortable/hideable columns of the offers table, in display order. */
const ALL_COLUMN_IDS = ['offer_number', 'created', 'sales_price', 'status', 'costs', 'profit', 'margin', 'description'] as const;

const COLUMN_STORAGE_KEY = 'leads:show:offers';

export default function OffersList({ lead, offers }: OffersListProps) {
    const { can } = useCan();
    const { t } = useTranslations();
    const { settings } = useSettings();
    // A row of eight columns does not survive a phone, so offers become cards there.
    const isMobile = useIsMobile();
    const approvalThreshold = settings.leadOfferApprovalThreshold;
    const [editingOffer, setEditingOffer] = useState<LeadOffer | null>(null);
    const [viewingOffer, setViewingOffer] = useState<LeadOffer | null>(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    // Everything here is client-side: the lead page already carries every offer,
    // so a round trip would only add latency.
    const [sortColumn, setSortColumn] = useState<string | null>('created');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>('desc');
    const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const {
        visibleColumns,
        showColumn: handleColumnShow,
        hideColumn: handleColumnHide,
    } = useColumnVisibility(COLUMN_STORAGE_KEY, ALL_COLUMN_IDS, ALL_COLUMN_IDS);
    const [rejectionModal, setRejectionModal] = useState<{ isOpen: boolean; offerId: string }>({
        isOpen: false,
        offerId: '',
    });

    const columnDefinitions = useMemo(
        () => [
            { id: 'offer_number', label: t('leads.offers.table.offer_number', 'Offer no.') },
            { id: 'created', label: t('leads.offers.table.created', 'Created') },
            { id: 'sales_price', label: t('leads.offers.table.sales_price', 'Sales Price') },
            { id: 'status', label: t('leads.offers.table.status', 'Status') },
            { id: 'costs', label: t('leads.offers.table.costs', 'Costs') },
            { id: 'profit', label: t('leads.offers.table.profit', 'Profit') },
            { id: 'margin', label: t('leads.offers.table.margin', 'Margin') },
            { id: 'description', label: t('leads.offers.table.description', 'Description') },
        ],
        [t],
    );

    // Status is the only thing worth filtering on; the rest of the table is
    // numbers that the search box already covers.
    const columnFilterOptions = useMemo(
        () => ({
            status: OFFER_STATUSES.map((status) => ({
                value: status,
                label: t(`leads.offers.status.${status}`, settingLabel(t, 'lead_offer_statuses', status)),
            })),
        }),
        [t],
    );

    const sortableColumns = useMemo(() => columnDefinitions.filter((column) => column.id !== 'description'), [columnDefinitions]);

    const handleFilterChange = (column: string, values: string[]) => {
        setColumnFilters((previous) => ({ ...previous, [column]: values }));
        setPage(1);
    };

    const toggleSingleFilter = (column: string, value: string) => {
        const current = columnFilters[column] ?? [];

        handleFilterChange(column, current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value]);
    };

    const clearFilters = () => {
        setColumnFilters({});
        setSearch('');
        setPage(1);
    };

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        setSortColumn(column);
        setSortDirection(direction);
        setPage(1);
    };

    const clearSort = () => {
        setSortColumn('created');
        setSortDirection('desc');
        setPage(1);
    };

    const getStatusBadge = (status: LeadOffer['status']) => {
        const statusColor =
            status === 'pending'
                ? 'bg-warning-subtle text-warning-subtle-foreground'
                : status === 'approved'
                  ? 'bg-success-subtle text-success-subtle-foreground'
                  : 'bg-danger-subtle text-danger-subtle-foreground';
        const statusLabels = {
            pending: t('leads.offers.status.pending', 'Pending'),
            approved: t('leads.offers.status.approved', 'Approved'),
            rejected: t('leads.offers.status.rejected', 'Rejected'),
        };
        return (
            <Badge variant="outline" className={statusColor}>
                {statusLabels[status] || status.charAt(0).toUpperCase() + status.slice(1)}
            </Badge>
        );
    };

    const handleApprove = async (offer: LeadOffer) => {
        if (!can('manage-sales-approvals')) {
            toast.warning(t('leads.offers.errors.no_permission_approve', 'You do not have permission to approve offers.'));
            return;
        }

        try {
            const response = await csrfFetch(`/leads/${lead.id}/offers/${offer.id}/approve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const result = await response.json().catch(() => ({}));
                throw new Error(result?.message || t('leads.offers.errors.failed_to_approve', 'Failed to approve offer'));
            }

            toast.success(t('leads.offers.messages.approved_successfully', 'Offer approved successfully'));
            router.reload({ only: ['lead', 'latestApprovedOffer'] });
        } catch (error) {
            console.error('Error approving offer:', error);
            toast.error(error instanceof Error ? error.message : t('leads.offers.errors.failed_to_approve', 'Failed to approve offer'));
        }
    };

    const handleReject = (offer: LeadOffer) => {
        setRejectionModal({ isOpen: true, offerId: String(offer.id) });
    };

    const handleConfirmRejection = async (offerId: string, reason: string) => {
        try {
            const response = await csrfFetch(`/leads/${lead.id}/offers/${offerId}/reject`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    comment: reason || null,
                }),
            });

            if (!response.ok) {
                const result = await response.json().catch(() => ({}));
                throw new Error(result?.message || t('leads.offers.errors.failed_to_reject', 'Failed to reject offer'));
            }

            toast.success(t('leads.offers.messages.rejected_successfully', 'Offer rejected successfully'));
            router.reload({ only: ['lead', 'latestApprovedOffer'] });
            setRejectionModal({ isOpen: false, offerId: '' });
        } catch (error) {
            console.error('Error rejecting offer:', error);
            toast.error(error instanceof Error ? error.message : t('leads.offers.errors.failed_to_reject', 'Failed to reject offer'));
        }
    };

    const calculateTotalCosts = (offer: LeadOffer): number => {
        // Use offer-specific costs if available, otherwise calculate from costLines
        if (offer.total_costs !== undefined) {
            return offer.total_costs;
        }
        if (offer.costLines && offer.costLines.length > 0) {
            return offer.costLines.reduce((sum, cost) => sum + cost.amount, 0);
        }
        return 0;
    };

    const calculateProfit = (offer: LeadOffer) => {
        const totalCosts = calculateTotalCosts(offer);
        return offer.sales_price - totalCosts;
    };

    const calculateProfitPercentage = (offer: LeadOffer) => {
        if (offer.sales_price <= 0) return 0;
        const profit = calculateProfit(offer);
        return (profit / offer.sales_price) * 100;
    };

    // Filtering runs on the client: every offer for this lead is already in the
    // page payload, so a round trip would only add latency.
    const visibleOffers = useMemo(() => {
        const needle = search.trim().toLowerCase();
        const statuses = columnFilters.status ?? [];

        return offers.filter((offer) => {
            if (statuses.length > 0 && !statuses.includes(offer.status)) {
                return false;
            }

            if (!needle) {
                return true;
            }

            // Searchable: the supplier's offer number, the description, and the
            // codes and names of its line items.
            const haystack = [
                offer.offer_number ?? '',
                offer.description ?? '',
                String(offer.sales_price ?? ''),
                ...(offer.lines ?? []).flatMap((line) => [line.code ?? '', line.name ?? '']),
            ]
                .join(' ')
                .toLowerCase();

            return haystack.includes(needle);
        });
    }, [offers, search, columnFilters.status]);

    const sortedOffers = useMemo(() => {
        const direction = sortDirection === 'asc' ? 1 : -1;

        return [...visibleOffers].sort((a, b) => {
            switch (sortColumn) {
                case 'offer_number':
                    return (a.offer_number ?? '').localeCompare(b.offer_number ?? '') * direction;
                case 'sales_price':
                    return (a.sales_price - b.sales_price) * direction;
                case 'costs':
                    return (calculateTotalCosts(a) - calculateTotalCosts(b)) * direction;
                case 'profit':
                    return (calculateProfit(a) - calculateProfit(b)) * direction;
                case 'margin':
                    return (calculateProfitPercentage(a) - calculateProfitPercentage(b)) * direction;
                case 'status':
                    return a.status.localeCompare(b.status) * direction;
                case 'created':
                default:
                    return (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * direction;
            }
        });
    }, [visibleOffers, sortColumn, sortDirection]);

    const pagination = useMemo(() => {
        const total = sortedOffers.length;
        const lastPage = Math.max(1, Math.ceil(total / perPage));
        const currentPage = Math.min(page, lastPage);
        const from = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
        const to = total === 0 ? 0 : Math.min(currentPage * perPage, total);
        const data = sortedOffers.slice((currentPage - 1) * perPage, currentPage * perPage);

        return {
            data,
            meta: {
                current_page: currentPage,
                last_page: lastPage,
                per_page: perPage,
                total,
                from,
                to,
            },
        };
    }, [sortedOffers, page, perPage]);

    const emptyMessage =
        search.trim() || (columnFilters.status ?? []).length > 0
            ? t('leads.offers.list.no_matches', 'No offers match your search.')
            : t('leads.offers.list.no_offers_yet', 'No offers yet. Create your first offer to get started.');

    if (!offers || offers.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-muted-foreground">{t('leads.offers.list.no_offers', 'No offers created yet.')}</p>
                <p className="text-muted-foreground mt-2 text-sm">{t('leads.offers.list.create_first', 'Create your first offer to get started.')}</p>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-4">
                {/* The same toolbar the index pages use, so filtering an offer
                    works exactly like filtering a lead. Everything is local:
                    the page already holds every offer for this lead. */}
                <GenericToolbar
                    quickFilterButtonLabel={
                        (columnFilters.status ?? []).length > 0
                            ? `${t('leads.index.toolbar.quick_filters', 'Quick filters')}: ${t('leads.offers.table.status', 'Status')}`
                            : t('leads.offers.filter.all_statuses', 'All statuses')
                    }
                    quickFilterDefinitions={[{ id: 'status', label: t('leads.offers.table.status', 'Status') }]}
                    columnDefinitions={columnDefinitions}
                    columnFilterOptions={columnFilterOptions}
                    columnFilters={columnFilters}
                    columnFilterSearch={columnFilterSearch}
                    setColumnFilterSearch={setColumnFilterSearch}
                    toggleSingleFilter={toggleSingleFilter}
                    handleFilterChange={handleFilterChange}
                    clearFilters={clearFilters}
                    isFilterOpen={isFilterOpen}
                    setIsFilterOpen={setIsFilterOpen}
                    isSortOpen={isSortOpen}
                    setIsSortOpen={setIsSortOpen}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    handleSortChange={handleSortChange}
                    clearSort={clearSort}
                    search={search}
                    setSearch={(value) => {
                        setSearch(value);
                        setPage(1);
                    }}
                    perPage={perPage}
                    onPerPageChange={(value) => {
                        setPerPage(value);
                        setPage(1);
                    }}
                    viewMode="table"
                    setViewMode={() => undefined}
                    // Offers are only ever a table, so the grid/table switch would
                    // be a control that does nothing.
                    showViewToggle={false}
                    visibleColumns={visibleColumns}
                    handleColumnShow={handleColumnShow}
                    handleColumnHide={handleColumnHide}
                    filteredCount={visibleOffers.length}
                    // resourceName only builds the toolbar's translation key
                    // (`<name>.index.toolbar.*`). Pointing it into the existing
                    // leads file gives the offers toolbar translated labels
                    // without standing up a whole new translation group.
                    resourceName="leads.offers"
                    resourceNamePlural={t('leads.offers.toolbar.resource_plural', 'offers')}
                    sortableColumns={sortableColumns}
                    excludeFromMoreFilters={['status']}
                />

                {/* Phones get one card per offer: the same figures and the same
                    actions, stacked instead of squeezed into eight columns. */}
                {isMobile ? (
                    <div className="space-y-3">
                        {pagination.data.length === 0 ? (
                            <Card>
                                <CardContent className="text-muted-foreground py-8 text-center text-sm">{emptyMessage}</CardContent>
                            </Card>
                        ) : (
                            pagination.data.map((offer) => {
                                const totalCosts = calculateTotalCosts(offer);
                                const profit = calculateProfit(offer);
                                const profitPercentage = calculateProfitPercentage(offer);
                                const canEdit = offer.status === 'pending';
                                const needsApproval = profitPercentage < approvalThreshold && offer.status === 'pending';
                                const canApproveOffer = can('manage-sales-approvals') && offer.status === 'pending';
                                const profitColor = profit > 0 ? 'text-success-subtle-foreground' : 'text-destructive';

                                return (
                                    <Card key={offer.id}>
                                        <CardContent className="space-y-3 p-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="font-medium break-words">
                                                        {offer.offer_number || <span className="text-muted-foreground">—</span>}
                                                    </p>
                                                    <p className="text-muted-foreground text-xs">
                                                        {format(new Date(offer.created_at), 'yyyy.MM.dd HH:mm')}
                                                    </p>
                                                </div>
                                                <div className="flex shrink-0 flex-col items-end gap-1">
                                                    {getStatusBadge(offer.status)}
                                                    {needsApproval && (
                                                        <Badge variant="outline" className="bg-warning-subtle text-warning-subtle-foreground">
                                                            {t('leads.offers.needs_approval', 'Needs Approval')}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-t pt-3">
                                                <div className="space-y-0.5">
                                                    <p className="text-muted-foreground text-xs font-medium">
                                                        {t('leads.offers.table.sales_price', 'Sales Price')}
                                                    </p>
                                                    <p className="text-sm font-semibold">{formatCurrency(offer.sales_price)}</p>
                                                </div>
                                                <div className="space-y-0.5">
                                                    <p className="text-muted-foreground text-xs font-medium">
                                                        {t('leads.offers.table.costs', 'Costs')}
                                                    </p>
                                                    <p className="text-sm font-semibold">{formatCurrency(totalCosts)}</p>
                                                </div>
                                                <div className="space-y-0.5">
                                                    <p className="text-muted-foreground text-xs font-medium">
                                                        {t('leads.offers.table.profit', 'Profit')}
                                                    </p>
                                                    <p className={cn('text-sm font-semibold', profitColor)}>{formatCurrency(profit)}</p>
                                                </div>
                                                <div className="space-y-0.5">
                                                    <p className="text-muted-foreground text-xs font-medium">
                                                        {t('leads.offers.table.margin', 'Margin')}
                                                    </p>
                                                    <p className={cn('text-sm font-semibold', profitColor)}>{profitPercentage.toFixed(2)}%</p>
                                                </div>
                                            </div>

                                            {/* The description stays out of the card — it reads in full
                                                in the details modal behind View. */}
                                            {offer.lines && offer.lines.length > 0 && (
                                                <div className="border-t pt-3">
                                                    <Badge variant="outline" className="font-normal">
                                                        {t('leads.offer_lines.count', ':count lines').replace(':count', String(offer.lines.length))}
                                                    </Badge>
                                                </div>
                                            )}

                                            <div className="flex flex-wrap items-center gap-2 border-t pt-3">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setViewingOffer(offer)}
                                                    className="h-10 flex-1 gap-2"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                    {t('leads.offers.actions.view', 'View')}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setEditingOffer(offer)}
                                                    className="h-10 flex-1 gap-2"
                                                    disabled={!canEdit}
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                    {t('leads.offers.actions.edit', 'Edit')}
                                                </Button>
                                                {canApproveOffer && needsApproval && (
                                                    <>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleApprove(offer)}
                                                            className="text-success-subtle-foreground hover:bg-success-subtle hover:text-success-subtle-foreground h-10 flex-1 gap-2"
                                                        >
                                                            <Check className="h-4 w-4" />
                                                            {t('leads.offers.actions.approve', 'Approve')}
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleReject(offer)}
                                                            className="text-destructive hover:bg-danger-subtle hover:text-destructive h-10 flex-1 gap-2"
                                                        >
                                                            <X className="h-4 w-4" />
                                                            {t('leads.offers.actions.reject', 'Reject')}
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })
                        )}
                    </div>
                ) : (
                    <div className="bg-card rounded-xl border px-4 py-2.5">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {columnDefinitions
                                        .filter((column) => visibleColumns.has(column.id))
                                        .map((column) => (
                                            <TableHead key={column.id}>{column.label}</TableHead>
                                        ))}
                                    <TableHead className="text-right">{t('common.actions', 'Actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pagination.data.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={visibleColumns.size + 1} className="text-muted-foreground py-8 text-center">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pagination.data.map((offer) => {
                                        const totalCosts = calculateTotalCosts(offer);
                                        const profit = calculateProfit(offer);
                                        const profitPercentage = calculateProfitPercentage(offer);
                                        const canEdit = offer.status === 'pending';
                                        const needsApproval = profitPercentage < approvalThreshold && offer.status === 'pending';
                                        const canApproveOffer = can('manage-sales-approvals') && offer.status === 'pending';
                                        const profitColor = profit > 0 ? 'text-success-subtle-foreground' : 'text-destructive';

                                        return (
                                            <TableRow key={offer.id}>
                                                {visibleColumns.has('offer_number') && (
                                                    <TableCell className="font-medium">
                                                        {offer.offer_number || <span className="text-muted-foreground">—</span>}
                                                    </TableCell>
                                                )}
                                                {visibleColumns.has('created') && (
                                                    <TableCell className="text-muted-foreground text-sm">
                                                        {format(new Date(offer.created_at), 'yyyy.MM.dd HH:mm')}
                                                    </TableCell>
                                                )}
                                                {visibleColumns.has('sales_price') && (
                                                    <TableCell className="font-medium">
                                                        <div className="flex items-center gap-2">
                                                            {formatCurrency(offer.sales_price)}
                                                            {/* A count only: this table already carries eight columns,
                                                        so the lines themselves live in the details modal. */}
                                                            {offer.lines && offer.lines.length > 0 && (
                                                                <Badge variant="outline" className="font-normal">
                                                                    {t('leads.offer_lines.count', ':count lines').replace(
                                                                        ':count',
                                                                        String(offer.lines.length),
                                                                    )}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                )}
                                                {visibleColumns.has('status') && (
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            {getStatusBadge(offer.status)}
                                                            {needsApproval && (
                                                                <Badge variant="outline" className="bg-warning-subtle text-warning-subtle-foreground">
                                                                    {t('leads.offers.needs_approval', 'Needs Approval')}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                )}
                                                {visibleColumns.has('costs') && <TableCell>{formatCurrency(totalCosts)}</TableCell>}
                                                {visibleColumns.has('profit') && (
                                                    <TableCell className={profitColor}>{formatCurrency(profit)}</TableCell>
                                                )}
                                                {visibleColumns.has('margin') && (
                                                    <TableCell className={profitColor}>{profitPercentage.toFixed(2)}%</TableCell>
                                                )}
                                                {visibleColumns.has('description') && (
                                                    <TableCell className="text-muted-foreground max-w-[280px] truncate text-sm">
                                                        {offer.description || '—'}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <div className="flex items-center justify-end gap-2">
                                                        {canApproveOffer && needsApproval && (
                                                            <>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleApprove(offer)}
                                                                    className="text-success-subtle-foreground hover:bg-success-subtle hover:text-success-subtle-foreground gap-2"
                                                                    title={t('leads.offers.actions.approve', 'Approve')}
                                                                >
                                                                    <Check className="h-4 w-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleReject(offer)}
                                                                    className="text-destructive hover:bg-danger-subtle hover:text-destructive gap-2"
                                                                    title={t('leads.offers.actions.reject', 'Reject')}
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </Button>
                                                            </>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setEditingOffer(offer)}
                                                            className="gap-2"
                                                            disabled={!canEdit}
                                                        >
                                                            <Edit2 className="h-4 w-4" />
                                                            <span className="hidden sm:inline">{t('leads.offers.actions.edit', 'Edit')}</span>
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => setViewingOffer(offer)} className="gap-2">
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}

                {pagination.meta.total > 0 ? (
                    <PaginationControls
                        pagination={pagination.meta}
                        onPageChange={setPage}
                        onPerPageChange={(next) => {
                            setPerPage(next);
                            setPage(1);
                        }}
                    />
                ) : null}
            </div>

            {editingOffer && <EditOfferModal lead={lead} offer={editingOffer} isOpen={!!editingOffer} onClose={() => setEditingOffer(null)} />}

            {viewingOffer && <OfferDetailsModal lead={lead} offer={viewingOffer} isOpen={!!viewingOffer} onClose={() => setViewingOffer(null)} />}

            <RejectionModal
                isOpen={rejectionModal.isOpen}
                offerId={rejectionModal.offerId}
                onConfirm={handleConfirmRejection}
                onCancel={() => setRejectionModal({ isOpen: false, offerId: '' })}
            />
        </>
    );
}
